import { css, notDesktop, styled } from "@obvio/app";
import { MapSection, RichText } from "@obvio/template";
import { Grid, Section } from "@obvio/ui";
import dynamic from "next/dynamic";

import { GolfSection } from "../GolfSection";
import { ImagesCarousel } from "../ImagesCarousel";

import type { ReactElement } from "react";

const LazyHero = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "Hero" */
    "./Renderers/Hero"
  );
  return comp.Hero;
});

const LazyMap = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "Map" */
    "./Renderers/Map"
  );
  return comp.GolfMap;
});

const LazyTiles = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "Tiles" */
    "./Renderers/Tiles"
  );
  return comp.Tiles;
});

const LazyTilesCta = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "Tiles" */
    "./Renderers/TilesCta"
  );
  return comp.TilesCta;
});

const LazyHeadingRichTextCtaImage = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "HeadingRichTextCtaImage" */
    "./Renderers/HeadingRichTextCtaImage"
  );
  return comp.HeadingRichTextCtaImage;
});

const LazyHeadingRichTextCtaImage2 = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "HeadingRichTextCtaImage" */
    "./Renderers/HeadingRichTextCtaImage2"
  );
  return comp.HeadingRichTextCtaImage2;
});

const LazyRichTextImage = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "RichTextImage" */
    "./Renderers/RichTextImage"
  );
  return comp.RichTextImage;
});

const LazyForm = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "Form" */
    "./Renderers/Form"
  );
  return comp.FormContact;
});

const LazyFormNewsletter = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "Form" */
    "./Renderers/FormNewsletter"
  );
  return comp.FormNewsletter;
});

const LazyBlocks = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "Blocks" */
    "./Renderers/Blocks"
  );
  return comp.Blocks;
});

const LazyArticlesLatest = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "ArticlesLatest" */
    "./Renderers/ArticlesLatest"
  );
  return comp.ArticlesLatest;
});

const LazyArticlesAll = dynamic(async () => {
  const comp = await import(
    /* webpackChunkName: "ArticlesAll" */
    "./Renderers/ArticlesAll"
  );
  return comp.ArticlesAll;
});

const MapSectionSpacing = styled(MapSection)`
  margin: 0;
  height: 97vh;
  > div {
    height: 100%;
  }
  @media ${notDesktop} {
    height: 30rem;
  }
`;

const HeroSection = styled(Section)`
  padding: 0;

  margin: 0;
  padding: 0 !important;
  max-width: 100vw !important;
  width: 100vw;
  position: relative;
  height: 105vh;
  max-height: 1280px;
  min-height: 70rem;

  > section {
    height: 100%;
  }
`;

const TextGrid = styled(Grid)`
  @media ${notDesktop} {
    grid-template-columns: 1fr;
  }
`;

export const customRenderers: (
  pageId?: string,
) => Record<string, (props: any) => ReactElement | null> = (_pageId) => ({
  hero: ({ description, images, video }) => (
    <HeroSection context="black">
      <LazyHero description={description} images={images} video={video} />
    </HeroSection>
  ),
  tiles: ({ tiles, __SETTINGS }) => (
    <GolfSection {...__SETTINGS}>
      <LazyTiles tiles={tiles} />
    </GolfSection>
  ),
  "tiles-cta": ({ tiles, __SETTINGS }) => (
    <GolfSection {...__SETTINGS}>
      <LazyTilesCta tiles={tiles} />
    </GolfSection>
  ),
  blocks: ({ accordion, __SETTINGS }) => (
    <div style={{ overflowX: "clip" }}>
      <GolfSection {...__SETTINGS} noFadeIn>
        <LazyBlocks blocks={accordion} />
      </GolfSection>
    </div>
  ),
  "heading-richText-cta-image": ({
    description,
    ctaText,
    url,
    image,
    __SETTINGS,
  }) => (
    <GolfSection {...__SETTINGS}>
      <LazyHeadingRichTextCtaImage
        description={description}
        ctaText={ctaText}
        url={url}
        image={image?.[0]}
      />
    </GolfSection>
  ),
  "heading-richText-image-richText-cta": ({
    description,
    image,
    description2,
    ctaText,
    url,
    __SETTINGS,
  }) => (
    <GolfSection {...__SETTINGS}>
      <LazyHeadingRichTextCtaImage2
        description={description}
        description2={description2}
        ctaText={ctaText}
        url={url}
        image={image?.[0]}
      />
    </GolfSection>
  ),
  "richText1-richText2": ({ richText1, richText2, __SETTINGS }) => (
    <GolfSection context="white" {...__SETTINGS}>
      <TextGrid templateColumns="1.5fr 1fr" gap="extraLarge">
        <div>
          <RichText value={richText1} />
        </div>
        <div>
          <RichText value={richText2} />
        </div>
      </TextGrid>
    </GolfSection>
  ),
  "richText-image": ({ description, image, imagePosition, __SETTINGS }) => (
    <GolfSection {...__SETTINGS}>
      <LazyRichTextImage
        description={description}
        image={image?.[0]}
        imagePosition={imagePosition}
      />
    </GolfSection>
  ),
  form: ({ title, description, description2, description3, __SETTINGS }) => (
    <GolfSection {...__SETTINGS}>
      <LazyForm
        title={title}
        description={description}
        description2={description2}
        description3={description3}
      />
    </GolfSection>
  ),
  "form-newsletter": ({ __SETTINGS }) => (
    <GolfSection {...__SETTINGS}>
      <LazyFormNewsletter />
    </GolfSection>
  ),
  "image-carousel": ({ imgs }) => <ImagesCarousel images={imgs} />,
  "articles-latest": ({ title, button, limit, __SETTINGS }) => (
    <GolfSection {...__SETTINGS}>
      <LazyArticlesLatest title={title} button={button} limit={limit} />
    </GolfSection>
  ),
  "articles-all": ({ __SETTINGS }) => (
    <GolfSection {...__SETTINGS}>
      <LazyArticlesAll />
    </GolfSection>
  ),
  map: ({ __SETTINGS }) => (
    <MapSectionSpacing
      outerCss={css`
        ${__SETTINGS.round &&
        css`
          border-top-left-radius: ${(theme) => theme.radius.big};
          border-top-right-radius: ${(theme) => theme.radius.big};
          overflow: hidden;
        `};
        margin-top: -5rem;
      `}
      {...__SETTINGS}
      id={__SETTINGS.name}
    >
      <LazyMap />
    </MapSectionSpacing>
  ),
});
